import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface Region {
  id: string;
  name: string;
  color: string;
  cities: string[];
  description: string;
}

const regions: Region[] = [
  {
    id: 'northern',
    name: 'Northern California',
    color: '#3B8686',
    cities: ['Sacramento', 'Redding', 'Chico', 'Eureka', 'Yuba City'],
    description: 'Serving hospitals and clinics from Sacramento to the Oregon border, including rural and underserved communities.',
  },
  {
    id: 'bay-area',
    name: 'San Francisco Bay Area',
    color: '#D4785C',
    cities: ['San Francisco', 'Oakland', 'San Jose', 'Palo Alto', 'Walnut Creek', 'Santa Rosa'],
    description: 'Comprehensive coverage across the Bay Area\'s major health systems, academic medical centers, and physician groups.',
  },
  {
    id: 'central-valley',
    name: 'Central Valley',
    color: '#6B8E6B',
    cities: ['Fresno', 'Bakersfield', 'Stockton', 'Modesto', 'Visalia', 'Merced'],
    description: 'Supporting agricultural communities and regional hospitals throughout the Central Valley corridor.',
  },
  {
    id: 'central-coast',
    name: 'Central Coast',
    color: '#7B8EAA',
    cities: ['Santa Barbara', 'San Luis Obispo', 'Monterey', 'Salinas', 'Santa Cruz'],
    description: 'Serving coastal healthcare facilities from Santa Cruz to Santa Barbara with rapid deployment capabilities.',
  },
  {
    id: 'southern',
    name: 'Southern California',
    color: '#C4956A',
    cities: ['Los Angeles', 'San Diego', 'Riverside', 'Irvine', 'Long Beach', 'Pasadena'],
    description: 'Our largest coverage zone, serving the greater Los Angeles, San Diego, and Inland Empire healthcare markets.',
  },
  {
    id: 'inland-empire',
    name: 'Inland Empire & Desert',
    color: '#9B7E9B',
    cities: ['Riverside', 'San Bernardino', 'Palm Springs', 'Temecula', 'Ontario'],
    description: 'Dedicated coverage for the rapidly growing Inland Empire and desert communities healthcare infrastructure.',
  },
];

export default function CaliforniaMap() {
  const [activeRegion, setActiveRegion] = useState<string>('bay-area');
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.05 });

  const active = regions.find((r) => r.id === activeRegion) || regions[1];

  return (
    <section id="regions" ref={ref} className="py-16 sm:py-24 px-4 sm:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div
          className={`text-center mb-12 sm:mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3">
            REGIONAL COVERAGE
          </p>
          <h2 className="text-navy text-[26px] sm:text-[36px] lg:text-[42px] font-semibold leading-tight mb-4">
            Statewide Service Regions
          </h2>
          <p className="text-[#5A6C7D] text-base sm:text-lg max-w-2xl mx-auto">
            Select a region to explore our coverage, partner facilities, and deployment capabilities in that area.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Map Area */}
          <div
            className={`lg:col-span-7 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="relative bg-[#F7F9FB] rounded-2xl p-6 sm:p-10 overflow-hidden">
              {/* Google Map Embed - California */}
              <div className="w-full h-[350px] sm:h-[480px] lg:h-[540px] rounded-xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d3300000!2d-119.4179!3d36.7783!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1718400000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Mediline Services California Coverage Map"
                  className="w-full h-full"
                ></iframe>
              </div>

              {/* Map Legend */}
              <div className="mt-6 flex flex-wrap gap-3">
                {regions.map((region) => (
                  <button
                    key={region.id}
                    onClick={() => setActiveRegion(region.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs sm:text-[13px] font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                      activeRegion === region.id
                        ? 'bg-navy text-white shadow-md scale-105'
                        : 'bg-white text-[#5A6C7D] hover:bg-navy/5 border border-gray-200'
                    }`}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: region.color }}
                    ></span>
                    {region.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Region Details Panel */}
          <div
            className={`lg:col-span-5 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            {/* Active Region Card */}
            <div className="bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 shadow-sm mb-6">
              <div className="flex items-center gap-3 mb-4">
                <span
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: active.color }}
                ></span>
                <h3 className="text-navy text-xl sm:text-2xl font-semibold">{active.name}</h3>
              </div>
              <p className="text-[#5A6C7D] text-sm sm:text-[15px] leading-relaxed mb-5">
                {active.description}
              </p>
              <div className="flex items-center gap-2 mb-5">
                <span className="w-5 h-5 flex items-center justify-center text-slate-blue">
                  <i className="ri-hospital-line text-lg"></i>
                </span>
                <span className="text-navy font-semibold text-base">Full Service Coverage</span>
              </div>
              <div>
                <p className="text-navy text-sm font-semibold mb-2">Key Service Cities</p>
                <div className="flex flex-wrap gap-2">
                  {active.cities.map((city) => (
                    <span
                      key={city}
                      className="bg-[#F7F9FB] text-[#5A6C7D] px-3 py-1.5 rounded-lg text-xs sm:text-[13px] font-medium"
                    >
                      {city}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Region List */}
            <div className="space-y-2">
              {regions.map((region) => (
                <button
                  key={region.id}
                  onClick={() => setActiveRegion(region.id)}
                  className={`w-full flex items-center justify-between px-5 py-4 rounded-xl transition-all duration-300 cursor-pointer group ${
                    activeRegion === region.id
                      ? 'bg-navy text-white shadow-md'
                      : 'bg-[#F7F9FB] text-navy hover:bg-navy/5'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0 transition-transform duration-300 group-hover:scale-125"
                      style={{ backgroundColor: region.color }}
                    ></span>
                    <span className="text-sm sm:text-[15px] font-medium">{region.name}</span>
                  </div>
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i
                      className={`ri-arrow-right-s-line text-lg transition-transform duration-300 ${
                        activeRegion === region.id ? 'translate-x-0.5' : 'group-hover:translate-x-0.5'
                      }`}
                    ></i>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
